import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const services = [
    'Business Setup',
    'Trade License',
    'Corporate Tax & VAT',
    'Visa Services',
    'Banking Assistance',
    'Office Space',
    'Other',
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
    });
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
              Let's Get <span className="text-[#DC143C]">Started</span>
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Operating from Chennai, we help businesses expand into the UAE. Contact us to begin your successful journey.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Contact Cards - Compact */}
            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="text-white" size={18} />
                </div>
                <h4 className="font-bold text-gray-800 text-sm">Phone</h4>
              </div>
              <p className="text-sm text-gray-600">+971 52 866 2076</p>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="text-white" size={18} />
                </div>
                <h4 className="font-bold text-gray-800 text-sm">Email</h4>
              </div>
              <p className="text-sm text-gray-600">info@firmzy.in</p>
              <p className="text-sm text-gray-600">support@firmzy.in</p>
            </div>

            <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#6B1B4D] to-[#DC143C] rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-white" size={18} />
                </div>
                <h4 className="font-bold text-gray-800 text-sm">Address</h4>
              </div>
              <p className="text-sm text-gray-600">
                123 Anna Salai, Teynampet<br />
                Chennai, Tamil Nadu 600018
              </p>
            </div>
          </div>

          {/* Compact Form */}
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Send us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-medium text-sm">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1 h-10"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-700 font-medium text-sm">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1 h-10"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-gray-700 font-medium text-sm">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+971 52 866 2076"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="mt-1 h-10"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-gray-700 font-medium text-sm">Service Required *</Label>
                  <Select 
                    value={formData.service} 
                    onValueChange={(value) => setFormData({ ...formData, service: value })}
                    required
                  >
                    <SelectTrigger className="mt-1 h-10">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service, idx) => (
                        <SelectItem key={idx} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-700 font-medium text-sm">How can we help? *</Label>
                <Textarea
                  id="message"
                  placeholder="Tell us about your requirements..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  className="mt-1 min-h-24"
                />
              </div>

              <Button 
                type="submit"
                className="w-full md:w-auto bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white hover:shadow-lg transition-all h-11 px-8 text-base group"
              >
                <Send className="mr-2 group-hover:translate-x-1 transition-transform" size={18} />
                Submit
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;